/**
 * O acesso de pesquisa (os cenários do Google WeatherNext 2, `acesso.ts`):
 *
 * - `DialogoDoAcesso`: usuário e senha. Só abre pelo link `?pesquisa` — nada
 *   no app aponta para ele (o dado não é para o público);
 * - `SobreOAcesso`: nos ajustes, com o acesso, o que os termos do Google pedem
 *   que acompanhe o dado (aviso, copyright, citação, modificações, a cópia dos
 *   termos) e "Sair do acesso de pesquisa".
 */
import { ExternalLink, LogOut } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Button, Dialog, Hint, Section } from '../ui/primitives';
import { entrarNaPesquisa, sairDaPesquisa } from './acesso';
import { TERMOS_WN2_URL } from './api';
import { definirModo } from './motor';
import { usePrevisaoStore } from './store';

const CAMPO =
  'mt-1 block min-h-10 w-full rounded-lg border border-ink-600 bg-ink-900 px-3 text-sm text-ink-100 focus-visible:outline-2 focus-visible:outline-violet-400 pointer-coarse:min-h-11';

export function DialogoDoAcesso() {
  const aberto = usePrevisaoStore((s) => s.pedirAcesso);
  const pesquisa = usePrevisaoStore((s) => s.pesquisa);
  const [usuario, setUsuario] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [lembrar, setLembrar] = useState(false);
  const senhaRef = useRef<HTMLInputElement>(null);
  const [estado, setEstado] = useState<'pronto' | 'vazio' | 'entrando' | 'recusado' | 'falhou'>('pronto');
  const id = useId();
  const fechar = () => {
    usePrevisaoStore.setState({ pedirAcesso: false });
    setSenha('');
    setEstado('pronto');
  };

  // Senha recusada: o foco volta à senha, selecionada, para digitar de novo.
  useEffect(() => {
    if (estado === 'recusado') senhaRef.current?.select();
  }, [estado]);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (estado === 'entrando') return;
    // O preenchimento automático (iOS) às vezes não avisa a mudança: lê o que está nos campos.
    const form = e.currentTarget as HTMLFormElement;
    const u = (new FormData(form).get('username') as string | null) ?? usuario;
    const p = (new FormData(form).get('password') as string | null) ?? senha;
    if (!u.trim() || !p) return setEstado('vazio');
    setEstado('entrando');
    const r = await entrarNaPesquisa(u, p, lembrar);
    if (r !== 'ok') return setEstado(r);
    setSenha('');
    setEstado('pronto');
    // Já dentro: o modo "Próximos dias" com os cenários no mapa.
    definirModo(true);
    usePrevisaoStore.getState().setVariavel('cenarios');
  };

  return (
    <Dialog open={aberto} onClose={fechar} title="Acesso de pesquisa" widthClass="max-w-md">
      {pesquisa ? (
        <p className="text-sm text-ink-200">Este navegador já tem o acesso de pesquisa. Os cenários do WeatherNext 2 estão no “Próximos dias”.</p>
      ) : (
        <form onSubmit={enviar} className="space-y-3" aria-describedby={`${id}-sobre`}>
          <p id={`${id}-sobre`} className="text-xs leading-snug text-ink-300">
            Os cenários do Google WeatherNext 2 (64 cenários, até 15 dias) são de acesso restrito: os termos do Google para a previsão em tempo real só
            permitem o uso interno e, para fins educacionais, a distribuição controlada a pessoas identificadas. Ao entrar, você concorda com os{' '}
            <a className="inline-flex items-center gap-0.5 underline" href={TERMOS_WN2_URL} target="_blank" rel="noreferrer">
              termos de uso do Google <ExternalLink className="size-3" aria-hidden />
            </a>{' '}
            e se compromete a não repassar os dados nem as imagens.
          </p>
          <label className="block text-xs font-medium text-ink-200">
            Usuário
            <input
              className={CAMPO}
              name="username"
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              aria-invalid={estado === 'recusado'}
            />
          </label>
          <label className="block text-xs font-medium text-ink-200">
            Senha
            <input
              ref={senhaRef}
              className={CAMPO}
              name="password"
              type={mostrar ? 'text' : 'password'}
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              autoComplete="current-password"
              aria-invalid={estado === 'recusado'}
            />
          </label>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-200">
            <label className="inline-flex min-h-9 items-center gap-2 pointer-coarse:min-h-11">
              <input type="checkbox" className="size-4 accent-violet-500" checked={mostrar} onChange={(e) => setMostrar(e.target.checked)} />
              Mostrar a senha
            </label>
            <label className="inline-flex min-h-9 items-center gap-2 pointer-coarse:min-h-11">
              <input type="checkbox" className="size-4 accent-violet-500" checked={lembrar} onChange={(e) => setLembrar(e.target.checked)} />
              Lembrar neste navegador
            </label>
          </div>
          <p className="text-[11px] text-ink-400">{lembrar ? 'Use só num aparelho seu.' : 'Sem lembrar, o acesso vale só nesta aba.'}</p>
          <div role="status" aria-live="polite">
            {estado === 'vazio' && <Hint>Preencha o usuário e a senha.</Hint>}
            {estado === 'recusado' && <Hint>Usuário ou senha incorretos.</Hint>}
            {estado === 'falhou' && <Hint>Não foi possível falar com o servidor agora. Tente de novo em alguns minutos.</Hint>}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" onClick={fechar}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={estado === 'entrando'}>
              {estado === 'entrando' ? 'Entrando…' : 'Entrar'}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

/** Nos ajustes, com o acesso: os termos do Google e "Sair". */
export function SobreOAcesso() {
  const pesquisa = usePrevisaoStore((s) => s.pesquisa);
  const termos = usePrevisaoStore((s) => s.indice?.termosWn2);
  if (!pesquisa) return null;
  return (
    <Section title="Acesso de pesquisa · WeatherNext 2" defaultOpen={false} storageKey="previsao-pesquisa">
      <div className="space-y-2 text-xs leading-snug text-ink-300" data-sobre-acesso="">
        <p>
          <strong className="text-ink-100">Restrito.</strong> Os cenários são do Google WeatherNext 2 (Google DeepMind), dados experimentais lidos pela
          cópia do dynamical.org. Não repasse os dados nem imagens deles: os termos só permitem a distribuição controlada, por isso não há exportação
          dos cenários.
        </p>
        <p className="font-medium text-ink-100">{termos?.copyright ?? 'Copyright 2024-6 Google LLC'}</p>
        {termos?.citacao && <p lang="en">{termos.citacao}</p>}
        {termos?.aviso && <p lang="en">{termos.aviso}</p>}
        {termos?.modificacoes && <p>{termos.modificacoes}</p>}
        <p>
          <a className="inline-flex items-center gap-0.5 underline" href={termos?.url ?? TERMOS_WN2_URL} target="_blank" rel="noreferrer">
            Termos de uso do Google <ExternalLink className="size-3" aria-hidden />
          </a>{' '}
          ·{' '}
          <a className="inline-flex items-center gap-0.5 underline" href="https://dynamical.org/catalog/google-weathernext2-forecast-operational-virtual/" target="_blank" rel="noreferrer">
            dynamical.org <ExternalLink className="size-3" aria-hidden />
          </a>
        </p>
        <button
          type="button"
          onClick={sairDaPesquisa}
          className="inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-ink-600 bg-ink-800 px-3 text-sm text-ink-100 hover:bg-ink-700 focus-visible:outline-2 focus-visible:outline-violet-400 pointer-coarse:min-h-11"
        >
          <LogOut className="size-4" aria-hidden />
          Sair do acesso de pesquisa
        </button>
      </div>
    </Section>
  );
}
